import { useState, useEffect } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, ShareNetwork, Copy, MusicNotesSimple } from "@phosphor-icons/react";

export function CatalogTab() {
  const [stats, setStats] = useState(null);
  const publicUrl = `${window.location.origin}/catalogo`;

  useEffect(() => {
    fetch(`${API}/public/catalog/genres`).then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Enlace copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar — selecciona y copia manualmente");
    }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`🎵 Escucha música hecha por la gente en Tu MusiKa:\n${publicUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="catalog-tab">
      <div className="rounded-2xl border-2 border-[#FFB800]/30 bg-gradient-to-br from-[#FFB800]/10 to-[#B4F34A]/5 p-6" data-testid="catalog-info-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#FFB800] flex items-center justify-center shrink-0">
            <ShareNetwork weight="fill" className="text-black" size={20} />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold text-white">Enlace público del catálogo</h3>
            <p className="text-sm text-white/60 mt-1">Compártelo con quien quieras.</p>
          </div>
        </div>

        <div className="rounded-xl bg-[#0a0a0a]/70 border border-white/10 p-4 mb-4">
          <p className="text-sm text-white/80 leading-relaxed">
            Este enlace muestra <strong className="text-[#FFB800]">todas las canciones generadas en la plataforma</strong>, organizadas y filtrables por género. Cualquier persona puede entrar y escucharlas sin necesidad de crear cuenta. Sólo se muestran datos públicos (título, nombre del artista que el usuario eligió, portada y audio). Nada de teléfonos, historias personales ni información privada.
          </p>
          <p className="text-xs text-white/50 mt-3">
            💡 Úsalo como <em>prueba social</em> en WhatsApp, redes sociales o newsletter para conseguir más usuarios. Conforme se generen canciones, el catálogo crece automáticamente.
          </p>
        </div>

        <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Tu enlace público</label>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input
            type="text"
            readOnly
            value={publicUrl}
            onClick={(e) => e.target.select()}
            className="flex-1 px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white/90 text-sm font-mono"
            data-testid="catalog-link-input"
          />
          <button
            onClick={copy}
            className="px-5 py-3 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold inline-flex items-center justify-center gap-2 transition-all active:scale-95"
            data-testid="catalog-link-copy"
          >
            <Copy weight="bold" size={16} /> Copiar
          </button>
        </div>

        <div className="flex gap-2">
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm inline-flex items-center justify-center gap-2"
            data-testid="catalog-link-open"
          >
            <MusicNotesSimple size={14} /> Abrir catálogo
          </a>
          <button
            onClick={shareWhatsApp}
            className="flex-1 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1DAE52] text-white text-sm font-semibold inline-flex items-center justify-center gap-2"
            data-testid="catalog-share-whatsapp"
          >
            <PaperPlaneTilt size={14} weight="fill" /> Compartir por WhatsApp
          </button>
        </div>
      </div>

      {stats && (
        <div className="rounded-2xl bg-[#121212] border border-white/5 p-5" data-testid="catalog-stats">
          <div className="flex items-baseline justify-between mb-4">
            <h4 className="font-display font-bold text-lg">Repertorio actual</h4>
            <span className="text-sm text-white/50">{stats.total} canciones públicas</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {stats.genres.map(g => (
              <div key={g.key} className="rounded-xl bg-[#0a0a0a] border border-white/5 p-3 flex items-center justify-between" data-testid={`catalog-genre-${g.key}`}>
                <span className="text-sm text-white/80">{g.name}</span>
                <span className={`text-sm font-display font-bold ${g.count > 0 ? 'text-[#FFB800]' : 'text-white/30'}`}>{g.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
